import React, { useState, useEffect } from 'react';
import { getDatabase, ref, set, onValue, remove } from 'firebase/database';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Delete, Download, Eye, Edit } from 'react-feather';
import { Modal, Button, ConfigProvider, Popconfirm, message } from 'antd';
import QRCode from 'qrcode';

// Import images
import edaplogo from "./img/edaplogo.png";
import unilislogo from "./img/unilislogo.png";
import bapSig from "./img/bap_sig.png";
import crlogo from "./img/crlogo.png";

import './attestationvalidation.css';

const AttestationValidationTemplate = ({ app }) => {
    const [formData, setFormData] = useState({
        noms: '',
        dateNaissance: '',
        lieuNaissance: '',
        prepositionLieuNaissance: 'à',
        pays: '',
        numeroEnregistrement: '',
        anneeAcademique: '',
        option: '',
        classe: '',
        titreSeminaire: '',
        note: '',
        dateEmission: new Date().toISOString().split('T')[0]
    });
    const [documents, setDocuments] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [qrCodeData, setQrCodeData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const db = getDatabase(app);

    // Load documents from Firebase
    useEffect(() => {
        const documentsRef = ref(db, `attestationvalidation/UNCR/`);
        onValue(documentsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const documentArray = Object.keys(data).map((key) => ({
                    id: key,
                    ...data[key],
                }));
                setDocuments(documentArray);
            }
        });
    }, [db]);

    // Generate QR Code
    useEffect(() => {
        const generateQRCode = async () => {
            if (!selectedDocument) return;

            let qrData = `ATTESTATION DE VALIDATION DU SEMINAIRE - UNILIS\n`;
            Object.keys(selectedDocument).forEach(key => {
                if (key !== 'createdAt' && selectedDocument[key]) {
                    qrData += `${key}: ${selectedDocument[key]}\n`;
                }
            });
            qrData += `Status: Document Verified\nIssued by: UNILIS - Université de Lisala`;

            try {
                const qrCodeUrl = await QRCode.toDataURL(qrData, {
                    width: 120,
                    margin: 1,
                    color: { dark: '#000000', light: '#FFFFFF' }
                });
                setQrCodeData(qrCodeUrl);
            } catch (error) {
                console.error('Error generating QR code:', error);
            }
        };
        if (selectedDocument) generateQRCode();
    }, [selectedDocument]);

    // Handle ESC key press to close modal
    useEffect(() => {
        const handleEsc = (event) => {
            if (event.key === 'Escape' && modalVisible) {
                handleModalClose();
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [modalVisible]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Validate required fields
        const requiredFields = ['noms', 'dateNaissance', 'lieuNaissance', 'prepositionLieuNaissance', 'pays', 'numeroEnregistrement', 'anneeAcademique', 'option', 'classe', 'titreSeminaire', 'note', 'dateEmission'];
        for (const field of requiredFields) {
            if (!formData[field] || formData[field].trim() === '') {
                alert(`${field} est obligatoire!`);
                return;
            }
        }

        // Validate note is a number between 0 and 20
        const noteNum = parseFloat(formData.note);
        if (isNaN(noteNum) || noteNum < 0 || noteNum > 20) {
            alert('La note doit être un nombre entre 0 et 20');
            return;
        }

        if (isEditing && editingId) {
            // Update existing document
            const documentRefPath = ref(db, `attestationvalidation/UNCR/${editingId}`);
            set(documentRefPath, {
                ...formData,
                createdAt: documents.find(doc => doc.id === editingId)?.createdAt || new Date().toISOString()
            })
                .then(() => {
                    console.log('Attestation de Validation document updated successfully');
                    message.success('Document modifié avec succès');
                    resetForm();
                })
                .catch((error) => {
                    console.error('Error updating Attestation de Validation document:', error);
                    message.error('Erreur lors de la modification');
                });
        } else {
            // Create new document
            const sanitizedName = formData.noms.replace(/[.#$[\]]/g, '_').replace(/\s+/g, '_');
            const documentRefPath = ref(db, `attestationvalidation/UNCR/${sanitizedName}_${Date.now()}`);
            set(documentRefPath, {
                ...formData,
                createdAt: new Date().toISOString()
            })
                .then(() => {
                    console.log('Attestation de Validation document saved successfully');
                    message.success('Document créé avec succès');
                    resetForm();
                })
                .catch((error) => {
                    console.error('Error saving Attestation de Validation document:', error);
                    message.error('Erreur lors de la création');
                });
        }
    };

    const resetForm = () => {
        setFormData({
            noms: '',
            dateNaissance: '',
            lieuNaissance: '',
            prepositionLieuNaissance: 'à',
            pays: '',
            numeroEnregistrement: '',
            anneeAcademique: '',
            option: '',
            classe: '',
            titreSeminaire: '',
            note: '',
            dateEmission: new Date().toISOString().split('T')[0]
        });
        setIsEditing(false);
        setEditingId(null);
    };

    const handleEditDocument = (document) => {
        setFormData({
            noms: document.noms || '',
            dateNaissance: document.dateNaissance || '',
            lieuNaissance: document.lieuNaissance || '',
            prepositionLieuNaissance: document.prepositionLieuNaissance || 'à',
            pays: document.pays || '',
            numeroEnregistrement: document.numeroEnregistrement || '',
            anneeAcademique: document.anneeAcademique || '',
            option: document.option || '',
            classe: document.classe || '',
            titreSeminaire: document.titreSeminaire || '',
            note: document.note || '',
            dateEmission: document.dateEmission || new Date().toISOString().split('T')[0]
        });
        setIsEditing(true);
        setEditingId(document.id);
        
        // Scroll to form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const showDocumentModal = (document) => {
        setSelectedDocument(document);
        setModalVisible(true);
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setSelectedDocument(null);
    };

    const handleDeleteDocument = (id) => {
        remove(ref(db, `attestationvalidation/UNCR/${id}`))
            .then(() => {
                message.success('Document supprimé avec succès');
            })
            .catch((error) => {
                console.error('Error deleting document:', error);
                message.error('Erreur lors de la suppression');
            });
    };

    const downloadPDF = async () => {
        if (!selectedDocument) return;

        const element = document.getElementById('attestation-validation-document');
        if (!element) {
            console.error('Document element not found');
            return;
        }

        const clone = element.cloneNode(true);
        clone.style.cssText = `
            width: 21cm !important;
            height: auto !important;
            min-height: 29.7cm !important;
            margin: 0 !important;
            padding: 2cm 1.5cm !important;
            background: white !important;
            position: relative !important;
            box-sizing: border-box !important;
            font-family: 'Times New Roman', serif !important;
            overflow: visible !important;
        `;

        const tempContainer = document.createElement('div');
        tempContainer.style.cssText = `
            position: fixed;
            top: -9999px;
            left: -9999px;
            z-index: -1;
            width: 21cm;
            height: auto;
            min-height: 29.7cm;
        `;
        tempContainer.appendChild(clone);
        document.body.appendChild(tempContainer);

        try {
            // Wait for images to load
            const images = clone.querySelectorAll('img');
            await Promise.all(Array.from(images).map(img => {
                return new Promise((resolve) => {
                    if (img.complete) {
                        resolve();
                    } else {
                        img.onload = resolve;
                        img.onerror = resolve;
                    }
                });
            }));

            // Small delay to ensure rendering is complete
            await new Promise(resolve => setTimeout(resolve, 500));

            const canvas = await html2canvas(clone, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#FFFFFF',
                logging: false,
                imageTimeout: 15000,
                scrollX: 0,
                scrollY: 0,
                width: clone.scrollWidth,
                height: clone.scrollHeight
            });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'pt', 'a4'); // 'p' for portrait
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Calculate the aspect ratio to fit the image properly
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
            const scaledWidth = imgWidth * ratio;
            const scaledHeight = imgHeight * ratio;
            // Center the image on the page
            const x = (pageWidth - scaledWidth) / 2;
            const y = (pageHeight - scaledHeight) / 2;
            pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
            pdf.save(`${selectedDocument.noms?.replace(/\s+/g, '_') || 'document'}_ATTESTATION_VALIDATION.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            message.error('Erreur lors de la génération du PDF');
        } finally {
            document.body.removeChild(tempContainer);
        }
    };

    return (
        <div className="attestation-validation-container">
            <div className="attestation-validation-form-section">
                <div style={{ textAlign: 'center' }}>
                    <img src={edaplogo} alt="Logo EDAP" className="attestation-validation-form-logo" />
                    <h2 style={{ textTransform: 'uppercase' }}>Attestation de Validation du Séminaire</h2>
                </div>

                <form onSubmit={handleSubmit} className="attestation-validation-form">
                    {/* Informations personnelles */}
                    <div className="attestation-validation-form-section-title">Informations personnelles</div>
                    <div className="attestation-validation-form-row">
                        <input
                            type="text"
                            name="noms"
                            value={formData.noms}
                            onChange={handleInputChange}
                            placeholder="Nom complet de l'étudiant"
                            required
                            className="attestation-validation-input"
                        />
                        <input
                            type="date"
                            name="dateNaissance"
                            value={formData.dateNaissance}
                            onChange={handleInputChange}
                            required
                            className="attestation-validation-input"
                        />
                    </div>

                    <div className="attestation-validation-form-row">
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <select
                                name="prepositionLieuNaissance"
                                value={formData.prepositionLieuNaissance}
                                onChange={handleInputChange}
                                className="attestation-validation-input"
                                style={{ width: '80px', flexShrink: 0 }}
                            >
                                <option value="à">à</option>
                                <option value="au">au</option>
                                <option value="en">en</option>
                                <option value="aux">aux</option>
                            </select>
                            <input
                                type="text"
                                name="lieuNaissance"
                                value={formData.lieuNaissance}
                                onChange={handleInputChange}
                                placeholder="Lieu de naissance"
                                required
                                className="attestation-validation-input"
                                style={{ flex: 1 }}
                            />
                        </div>
                        <input
                            type="text"
                            name="pays"
                            value={formData.pays}
                            onChange={handleInputChange}
                            placeholder="Pays"
                            required
                            className="attestation-validation-input"
                        />
                    </div>

                    <div className="attestation-validation-form-row">
                        <input
                            type="text"
                            name="numeroEnregistrement"
                            value={formData.numeroEnregistrement}
                            onChange={handleInputChange}
                            placeholder="Numéro d'enregistrement"
                            required
                            className="attestation-validation-input"
                        />
                        <input
                            type="text"
                            name="anneeAcademique"
                            value={formData.anneeAcademique}
                            onChange={handleInputChange}
                            placeholder="Année académique (Ex: 2024-2025)"
                            required
                            className="attestation-validation-input"
                        />
                    </div>

                    {/* Informations académiques */}
                    <div className="attestation-validation-form-section-title">Informations académiques</div>
                    <div className="attestation-validation-form-row">
                        <input
                            type="text"
                            name="option"
                            value={formData.option}
                            onChange={handleInputChange}
                            placeholder="Option"
                            required
                            className="attestation-validation-input"
                        />
                        <input
                            type="text"
                            name="classe"
                            value={formData.classe}
                            onChange={handleInputChange}
                            placeholder="Classe (ex: MPhil, PhD I, PhD II, PhD III, HDR)"
                            required
                            className="attestation-validation-input"
                        />
                    </div>

                    <div className="attestation-validation-form-row">
                        <textarea
                            name="titreSeminaire"
                            value={formData.titreSeminaire}
                            onChange={handleInputChange}
                            placeholder="Titre du séminaire"
                            required
                            className="attestation-validation-input"
                            style={{ height: '100px', resize: 'vertical' }}
                        />
                    </div>

                    <div className="attestation-validation-form-row">
                        <input
                            type="number"
                            name="note"
                            value={formData.note}
                            onChange={handleInputChange}
                            placeholder="Note (sur 20)"
                            required
                            min="0"
                            max="20"
                            step="0.1"
                            className="attestation-validation-input"
                        />
                        <input
                            type="date"
                            name="dateEmission"
                            value={formData.dateEmission}
                            onChange={handleInputChange}
                            required
                            className="attestation-validation-input"
                        />
                    </div>

                    <button type="submit" className="attestation-validation-submit-btn">
                        {isEditing ? 'Modifier l\'Attestation' : 'Créer l\'Attestation'}
                    </button>
                    {isEditing && (
                        <button 
                            type="button" 
                            onClick={resetForm}
                            className="attestation-validation-submit-btn"
                            style={{ 
                                marginTop: '10px', 
                                background: 'linear-gradient(135deg, #6c757d, #5a6268)' 
                            }}
                        >
                            Annuler
                        </button>
                    )}
                </form>

                {/* Document List */}
                <div className="attestation-validation-document-list">
                    {documents.map((document) => (
                        <div key={document.id} className="attestation-validation-document-item">
                            <button
                                className="attestation-validation-document-toggle"
                                onClick={() => showDocumentModal(document)}
                            >
                                <Eye size={16} style={{ marginRight: '8px' }} />
                                {document.noms} - Attestation de Validation
                            </button>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button 
                                    className="attestation-validation-edit-btn"
                                    onClick={() => handleEditDocument(document)}
                                    title="Modifier"
                                >
                                    <Edit size={15} />
                                </button>
                                <Popconfirm
                                    title="Supprimer le document"
                                    description="Êtes-vous sûr de vouloir supprimer ce document ?"
                                    onConfirm={() => handleDeleteDocument(document.id)}
                                    okText="Oui"
                                    cancelText="Non"
                                    okButtonProps={{ danger: true }}
                                >
                                    <button className="attestation-validation-delete-btn" title="Supprimer">
                                        <Delete size={15} />
                                    </button>
                                </Popconfirm>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal */}
            <ConfigProvider>
                <Modal
                    title={<span style={{ color: 'white' }}>Aperçu - Attestation de Validation du Séminaire</span>}
                    open={modalVisible}
                    onCancel={handleModalClose}
                    width="95vw"
                    style={{ top: 10 }}
                    centered={false}
                    maskClosable={true}
                    keyboard={true}
                    footer={[
                        <Button
                            key="download"
                            type="primary"
                            icon={<Download size={16} />}
                            onClick={downloadPDF}
                            style={{
                                backgroundColor: '#28a745',
                                borderColor: '#28a745',
                                fontWeight: 'bold'
                            }}
                        >
                            Télécharger PDF
                        </Button>,
                        <Button
                            key="close"
                            onClick={handleModalClose}
                            style={{ fontWeight: 'bold' }}
                        >
                            Fermer
                        </Button>
                    ]}
                >
                    {selectedDocument && (
                        <div className="attestation-validation-modal-content">
                            <AttestationValidationDocument document={selectedDocument} qrCodeData={qrCodeData} />
                        </div>
                    )}
                </Modal>
            </ConfigProvider>
        </div>
    );
};

// Document Component
const AttestationValidationDocument = ({ document: docData, qrCodeData }) => {
    const formatDateWithMonthName = (dateStr) => {
        const date = new Date(dateStr);
        const months = [
            "janvier", "février", "mars", "avril", "mai", "juin",
            "juillet", "août", "septembre", "octobre", "novembre", "décembre"
        ];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const formatDateDDMMYYYY = (dateStr) => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    return (
        <div id="attestation-validation-document" className="attestation-validation-document">
            {/* Header */}
            <div className="attestation-validation-header">
                <div className="attestation-validation-header-row">
                    <div className="attestation-validation-left-logo">
                        <img src={unilislogo} alt="Logo UNILIS" className="attestation-validation-logo" />
                        <div className="attestation-validation-left-text">
                            <h1 className="attestation-validation-school">ECOLE DOCTORALE</h1>
                            <h2 className="attestation-validation-subtitle">Cotutelle Internationale de Thèse et Co-diplomation</h2>
                        </div>
                    </div>
                    <div className="attestation-validation-header-center">
                        <h3 className="attestation-validation-university">UNIVERSITE DE LISALA</h3>
                        <p className="attestation-validation-center">Centre Interuniversitaire de Recherche Pluridisciplinaire (Cirep)</p>
                        <p className="attestation-validation-status">Status: Universite publique</p>
                        <p className="attestation-validation-web">Web: www.universitedelisala.ac.cd</p>
                        <p className="attestation-validation-email">Email: info@universitedelisala.ad.cd</p>
                    </div>
                    <div className="attestation-validation-right-logo">
                        <img src={crlogo} alt="Logo CRN" className="attestation-validation-logo" />
                    </div>
                </div>
            </div>

            {/* Black separator line */}
            <div className="attestation-validation-separator"></div>

            {/* Title */}
            <div className="attestation-validation-title-section">
                <h1 className="attestation-validation-main-title">ATTESTATION DE VALIDATION DU SÉMINAIRE</h1>
            </div>

            {/* Document Body */}
            <div className="attestation-validation-document-body">
                <div className="attestation-validation-main-text">
                    <p>
                        Je soussigné Pr NIZEYIMANA Jean Baptiste; Directeur Général du Centre Interuniversitaire de Recherche Pluridisciplinaire (CIREP) atteste par la présente que <strong>{docData.noms}</strong>, né(e) le <strong>{formatDateDDMMYYYY(docData.dateNaissance)}</strong> {docData.prepositionLieuNaissance || 'à'} <strong>{docData.lieuNaissance}</strong>, <strong>{docData.pays}</strong>, enregistré(e) sous No <strong>{docData.numeroEnregistrement}</strong>; est régulièrement inscrit(e) au programme en cotutelle internationale et Codiplomation entre l'Université de Lisala en République Démocratique du Congo et UNCR University aux Etats Unis en Classe: <strong>{docData.classe}</strong>; année académique: <strong>{docData.anneeAcademique}</strong>; Option: <strong>{docData.option}</strong>, <br/>
                        a participé au Séminaire intitulé : <strong>{docData.titreSeminaire}</strong><br/>
                        L'impétrant a validé le Séminaire avec la note: <strong>{docData.note}</strong> / 20<br/><br/>
                        Le présent certificat lui est délivré pour servir et valoir ce que de droit.<br/><br/>
                        Lisala, le <strong>{formatDateWithMonthName(docData.dateEmission)}</strong>
                    </p>
                </div>
            </div>

            {/* Signature */}
            <div className="attestation-validation-signature">
                <div className="attestation-validation-signature-content">
                    <img src={bapSig} alt="Signature du Directeur Général" className="attestation-validation-signature-image" />
                    <p className="attestation-validation-signature-name">Pr NIZEYIMANA Jean Baptiste</p>
                    <p className="attestation-validation-signature-title">Directeur Général du CIREP</p>
                </div>
                {qrCodeData && (
                    <div className="attestation-validation-qr-section">
                        <img
                            src={qrCodeData}
                            alt="QR Code - Document Verification"
                            className="qr-code"
                            style={{
                                width: '60px',
                                height: '60px',
                                border: '1px solid #000'
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AttestationValidationTemplate;

